import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

const sanitizeForFirestore = (value) => {
  if (value === undefined) return null;
  if (value === null) return null;
  if (Array.isArray(value)) return value.map(sanitizeForFirestore);
  if (typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, nestedValue]) => {
      acc[key] = sanitizeForFirestore(nestedValue);
      return acc;
    }, {});
  }
  return value;
};

const actorNameFrom = ({ userData, user }) => {
  const name = [
    userData?.FirstName,
    userData?.LastName,
  ].filter(Boolean).join(' ').trim();

  return name || userData?.FacilityName || user?.displayName || user?.email || 'Unknown User';
};

export const buildClassActivityActor = ({ user, userDocId, userData } = {}) => ({
  id: userDocId || user?.uid || null,
  email: user?.email || userData?.Email || userData?.LocalizedEmail || userData?.email || null,
  name: actorNameFrom({ userData, user }),
});

const buildClassActivityDocument = ({
  action,
  title,
  body,
  actor,
  changes = {},
  metadata = {},
  type = 'note',
  source = 'admin_portal',
}) => sanitizeForFirestore({
  action,
  title: title || action,
  body: body || title || action,
  type,
  source,
  timestamp: serverTimestamp(),
  timestampMs: Date.now(),
  actor: actor || buildClassActivityActor(),
  changes,
  metadata,
});

export const logClassActivity = async (db, classId, {
  action,
  title,
  body,
  actor,
  changes = {},
  metadata = {},
  type = 'note',
  source = 'admin_portal',
} = {}) => {
  if (!db || !classId || !action) return null;

  return addDoc(collection(db, 'Classes', classId, 'Activity'), buildClassActivityDocument({
    action,
    title,
    body,
    type,
    source,
    actor,
    changes,
    metadata: {
      classId,
      ...metadata,
    },
  }));
};

const getClassActivityMetadata = (classId, classData = {}) => ({
  classId,
  facilityId: classData.FacilityID || null,
  className: classData.ClassName || classData.ClassType || null,
  classDate: classData.Date || null,
});

export const buildInstructorClassClaimActivity = ({
  actor,
  instructorId,
  classId,
  classData = {},
} = {}) => buildClassActivityDocument({
  action: 'class_claimed',
  title: 'Class Claimed',
  body: `${actor?.name || 'Instructor'} claimed this class.`,
  actor,
  source: 'instructor_portal',
  changes: {
    AssignedInstructorID: {
      from: classData.AssignedInstructorID || '',
      to: instructorId || '',
    },
  },
  metadata: getClassActivityMetadata(classId, classData),
});

export const buildInstructorClassDropActivity = ({
  actor,
  instructorId,
  classId,
  classData = {},
  reason,
  isWithinTwoHours = false,
  isWithinTwentyFourHours = false,
  isStrikeEligible = false,
} = {}) => buildClassActivityDocument({
  action: 'instructor_requested_sub',
  title: 'Instructor Requested Sub',
  body: `${actor?.name || 'Instructor'} requested a sub${reason ? `: ${reason}` : '.'}`,
  actor,
  source: 'instructor_portal',
  changes: {
    AssignedInstructorID: {
      from: classData.AssignedInstructorID || instructorId || '',
      to: isWithinTwoHours ? classData.AssignedInstructorID || instructorId || '' : '',
    },
    classStatus: {
      from: classData.classStatus,
      to: isWithinTwoHours ? 4 : 3,
    },
  },
  metadata: {
    ...getClassActivityMetadata(classId, classData),
    reason: reason || null,
    droppedInstructorId: instructorId || classData.AssignedInstructorID || null,
    isWithinTwoHours,
    isWithinTwentyFourHours,
    isStrikeEligible,
  },
});

export const buildInstructorClassCompletionActivity = ({
  actor,
  classId,
  classData = {},
  update = {},
  drove,
  people,
  expected,
  feedback,
  concern,
} = {}) => buildClassActivityDocument({
  action: 'class_completion_submitted',
  title: 'Class Completion Submitted',
  body: `${actor?.name || 'Instructor'} submitted the class completion form.`,
  actor,
  source: 'instructor_portal',
  changes: {
    status: {
      from: classData.status,
      to: update.status,
    },
    People: {
      from: classData.People,
      to: update.People,
    },
  },
  metadata: {
    ...getClassActivityMetadata(classId, classData),
    drove: Boolean(drove),
    people: people === '' || people === undefined ? null : people,
    expected: expected || null,
    feedback: feedback || null,
    concern: concern || null,
  },
});
